import * as Linking from "expo-linking";
import { CheckCircle2, MapPin, UsersRound } from "lucide-react-native";
import { useEffect, useMemo, useState } from "react";
import {
  Pressable,
  StyleSheet,
  Text,
  useColorScheme,
  View,
} from "react-native";
import QRCode from "react-native-qrcode-svg";

import {
  CentralPublicEvent,
  generatePublicCentralEventUrl,
  getCentralEventGuestErrorMessage,
  submitPublicCentralEventResponse,
} from "../lib/central-event-guests";
import type { PublicCentralMemorial } from "../lib/public-central-memorial";
import { central, spacing } from "../theme/tokens";
import { PublicAttendanceCheckIn } from "./PublicAttendanceCheckIn";
import { Button, Field, Panel, SegmentedPill } from "./ui";

type EventMode = "rsvp" | "check-in";

export function PublicEventExperience({
  funeralHomeId,
  initialEventId,
  initialMode,
  memorial,
  slug,
  brand = "default",
}: {
  funeralHomeId: string;
  initialEventId: string;
  initialMode: EventMode;
  memorial: PublicCentralMemorial;
  slug: string;
  brand?: "default" | "monarch";
}) {
  const colorScheme = useColorScheme();
  const publicTheme =
    memorial.theme.modes[colorScheme === "dark" ? "dark" : "light"];
  const events = useMemo(
    () => memorial.serviceEvents.map(normalizeEvent).filter((event) => Boolean(event.id && event.title)),
    [memorial.serviceEvents],
  );
  const monarchBrand = brand === "monarch";
  const brandColors = monarchBrand
    ? { border: "#D6DDE8", muted: "#4C5C73", panel: "#FFFFFF", primary: "#10265E", text: "#14233D" }
    : { border: central.border.subtle, muted: central.text.muted, panel: central.surface.raised, primary: central.action.primary, text: central.text.primary };
  const initialEvent = events.find((event) => event.id === initialEventId) ?? events[0];
  const checkInAvailable = memorial.checkInEnabled;
  const rsvpAvailable = memorial.rsvpEnabled;
  const preferredMode: EventMode = initialMode === "check-in" && checkInAvailable
    ? "check-in"
    : rsvpAvailable
      ? "rsvp"
      : "check-in";
  const [eventId, setEventId] = useState(initialEvent?.id ?? "");
  const [mode, setMode] = useState<EventMode>(preferredMode);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [complete, setComplete] = useState<"rsvp" | "check-in" | "">("");
  const [receiptToken, setReceiptToken] = useState("");
  const [form, setForm] = useState({
    consent: false,
    email: "",
    name: "",
    note: "",
    partySize: "1",
    phone: "",
    relationship: "",
    responseStatus: "attending" as "attending" | "not_attending",
  });
  const event = events.find((candidate) => candidate.id === eventId);
  const approvedMap = memorial.maps.find(
    (candidate) =>
      candidate.sourceType === "event" &&
      candidate.sourceId === event?.id &&
      candidate.status === "confirmed",
  );

  useEffect(() => {
    if (initialEvent?.id) setEventId(initialEvent.id);
    setMode(preferredMode);
    setComplete("");
  }, [initialEvent?.id, preferredMode]);

  useEffect(() => {
    if (typeof window === "undefined") return;
    setReceiptToken(
      new URLSearchParams(window.location.hash.slice(1)).get("receipt") ?? "",
    );
  }, []);

  if (!events.length) return null;
  if (initialEventId && !events.some((candidate) => candidate.id === initialEventId)) {
    return <Panel style={styles.panel}><Text style={styles.title}>This event is no longer available.</Text><Text style={styles.muted}>Choose the main memorial link for the current service schedule.</Text></Panel>;
  }
  if (!event) return null;

  if (!checkInAvailable && !rsvpAvailable) return <Panel style={styles.panel}><Text style={styles.title}>Service details</Text><Text style={styles.muted}>Online RSVP and check-in are not enabled for this memorial.</Text></Panel>;
  const publicUrl = generatePublicCentralEventUrl(funeralHomeId, slug, event.id, mode);
  const canSubmit = form.name.trim().length >= 2 && Boolean(form.email.trim() || form.phone.trim());

  const submit = async () => {
    if (!canSubmit || busy) return;
    setBusy(true);
    setError("");
    try {
      await submitPublicCentralEventResponse({
        action: mode === "check-in" ? "check_in" : "rsvp",
        consent: form.consent ? "marketing" : "",
        email: form.email,
        eventId: event.id,
        eventKey: operationKey(mode),
        funeralHomeId,
        name: form.name,
        note: form.note,
        partySize: Number(form.partySize) || 1,
        phone: form.phone,
        relationship: form.relationship,
        responseStatus: form.responseStatus,
        slug,
      });
      setComplete(mode);
    } catch (caught) {
      setError(getCentralEventGuestErrorMessage(caught));
    } finally {
      setBusy(false);
    }
  };

  return (
    <View style={styles.section}>
      <View style={styles.heading}>
        <Text style={[styles.title, { color: monarchBrand ? brandColors.text : publicTheme.text }]}> 
          Service events
        </Text>
        <Text style={[styles.muted, { color: monarchBrand ? brandColors.muted : publicTheme.mutedText }]}> 
          Choose the gathering you plan to attend or are arriving for.
        </Text>
      </View>
      <View style={styles.pills} accessibilityRole="radiogroup">
        {events.map((candidate) => (
          <SegmentedPill
            active={candidate.id === event.id}
            brand={monarchBrand ? "monarch" : "default"}
            key={candidate.id}
            label={candidate.title}
            onPress={() => { setEventId(candidate.id); setComplete(""); setError(""); }}
            selectionRole="radio"
          />
        ))}
      </View>

      <Panel style={[styles.eventPanel, { backgroundColor: brandColors.panel, borderColor: brandColors.border }]}>
        <Text style={[styles.eventTitle, { color: brandColors.text }]}>{event.title}</Text>
        {event.startAt ? <Text style={[styles.eventTime, { color: brandColors.muted }]}>{formatDateTime(event.startAt)}{event.endAt ? ` – ${formatTime(event.endAt)}` : ""}</Text> : null}
        {event.venue || approvedMap?.displayAddress || event.address ? (
          <View style={styles.detailRow}>
            <MapPin color={brandColors.primary} size={17} />
            <View style={styles.locationCopy}>
              <Text style={[styles.detailText, { color: brandColors.muted }]}>
                {[event.venue, approvedMap?.displayAddress || event.address]
                  .filter(Boolean)
                  .join(" · ")}
              </Text>
              {approvedMap?.manualDirections ? (
                <Text style={[styles.directionText, { color: brandColors.muted }]}>
                  Directions: {approvedMap.manualDirections}
                </Text>
              ) : null}
              {approvedMap?.directionsUrl ? (
              <Button
                compact
                brand={monarchBrand ? "monarch" : "default"}
                label="Open approved map"
                  onPress={() =>
                    void Linking.openURL(approvedMap.directionsUrl)
                  }
                  shape="rounded"
                  variant="secondary"
                />
              ) : (
                <Text style={[styles.mapFallback, { color: brandColors.muted }]}>
                  Map unavailable. Use the approved address above.
                </Text>
              )}
            </View>
          </View>
        ) : null}
        {event.livestreamUrl ? <Button brand={monarchBrand ? "monarch" : "default"} compact label="Open livestream" variant="secondary" shape="rounded" onPress={() => void Linking.openURL(event.livestreamUrl)} /> : null}
      </Panel>

      <View style={styles.modeRow} accessibilityRole="radiogroup">
        {rsvpAvailable ? <SegmentedPill active={mode === "rsvp"} brand={monarchBrand ? "monarch" : "default"} label="RSVP" selectionRole="radio" onPress={() => { setMode("rsvp"); setComplete(""); }} /> : null}
        {checkInAvailable ? <SegmentedPill active={mode === "check-in"} brand={monarchBrand ? "monarch" : "default"} label="Check in now" selectionRole="radio" onPress={() => { setMode("check-in"); setComplete(""); }} /> : null}
      </View>

      {mode === "check-in" ? (
        <PublicAttendanceCheckIn
          eventId={event.id}
          eventLabel={event.title}
          brand={monarchBrand ? "monarch" : "default"}
          funeralHomeId={funeralHomeId}
          initialReceiptToken={receiptToken}
          key={`${event.id}-${receiptToken || "new"}`}
          slug={slug}
          source={
            memorial.qrEnabled && initialEventId === event.id
              ? "qr"
              : "direct"
          }
        />
      ) : complete ? (
        <Panel style={styles.success}>
          <CheckCircle2 color="#10265E" size={30} />
          <View style={styles.successCopy}>
            <Text style={styles.successTitle}>{complete === "rsvp" ? "Your RSVP is recorded." : "You are checked in."}</Text>
            <Text style={styles.muted}>{complete === "rsvp" ? "The funeral home can now see your response on the event guest list." : "The front desk can see your arrival now."}</Text>
          </View>
        </Panel>
      ) : (
        <Panel style={[styles.formPanel, { backgroundColor: brandColors.panel, borderColor: brandColors.border }]}>
          <View style={styles.formHeading}>
          <UsersRound color={brandColors.primary} size={22} />
            <View style={styles.formHeadingCopy}>
              <Text style={[styles.formTitle, { color: brandColors.text }]}>RSVP for this event</Text>
              <Text style={[styles.muted, { color: brandColors.muted }]}>Your name and one contact method are required so the funeral home can match your response.</Text>
            </View>
          </View>
          <View style={styles.fields}>
            <Field brand={monarchBrand ? "monarch" : "default"} containerStyle={styles.field} label="Full name" required value={form.name} onChangeText={(name) => setForm((current) => ({ ...current, name }))} placeholder="Your full name" autoCapitalize="words" />
            <Field brand={monarchBrand ? "monarch" : "default"} containerStyle={styles.field} label="Email" value={form.email} onChangeText={(email) => setForm((current) => ({ ...current, email }))} placeholder="you@example.com" keyboardType="email-address" autoCapitalize="none" />
            <Field brand={monarchBrand ? "monarch" : "default"} containerStyle={styles.field} label="Phone" value={form.phone} onChangeText={(phone) => setForm((current) => ({ ...current, phone }))} placeholder="(555) 555-0184" keyboardType="phone-pad" />
            <Field brand={monarchBrand ? "monarch" : "default"} containerStyle={styles.field} label="Relationship" value={form.relationship} onChangeText={(relationship) => setForm((current) => ({ ...current, relationship }))} placeholder="Friend, relative, colleague" />
            {mode === "rsvp" ? <Field brand={monarchBrand ? "monarch" : "default"} containerStyle={styles.field} label="Party size" value={form.partySize} onChangeText={(partySize) => setForm((current) => ({ ...current, partySize }))} placeholder="1" keyboardType="number-pad" /> : null}
            {mode === "rsvp" ? <Field brand={monarchBrand ? "monarch" : "default"} containerStyle={styles.fullField} label="Note for the funeral home" multiline value={form.note} onChangeText={(note) => setForm((current) => ({ ...current, note }))} placeholder="Accessibility, seating, or other helpful details" /> : null}
          </View>
          {mode === "rsvp" ? <View style={styles.modeRow}><Button brand={monarchBrand ? "monarch" : "default"} compact label="Attending" variant={form.responseStatus === "attending" ? "primary" : "secondary"} shape="rounded" onPress={() => setForm((current) => ({ ...current, responseStatus: "attending" }))} /><Button brand={monarchBrand ? "monarch" : "default"} compact label="Unable to attend" variant={form.responseStatus === "not_attending" ? "primary" : "secondary"} shape="rounded" onPress={() => setForm((current) => ({ ...current, responseStatus: "not_attending" }))} /></View> : null}
          <Pressable accessibilityRole="checkbox" accessibilityState={{ checked: form.consent }} onPress={() => setForm((current) => ({ ...current, consent: !current.consent }))} style={styles.consentRow}>
            <Text style={styles.checkbox}>{form.consent ? "✓" : ""}</Text>
            <Text style={styles.consentText}>I would like the funeral home to contact me later with planning and pre-arrangement information. This is optional.</Text>
          </Pressable>
          {error ? <Text accessibilityLiveRegion="assertive" style={styles.error}>{error}</Text> : null}
          <Button brand={monarchBrand ? "monarch" : "default"} label="Submit RSVP" loading={busy} disabled={!canSubmit || busy} shape="rounded" onPress={() => void submit()} />
        </Panel>
      )}

      <Panel style={[styles.qrPanel, { backgroundColor: brandColors.panel, borderColor: brandColors.border }]}>
        <View style={styles.qrCopy}><Text style={[styles.formTitle, { color: brandColors.text }]}>Share this exact event</Text><Text selectable style={[styles.url, { color: brandColors.primary }]}>{publicUrl}</Text></View>
        <View style={styles.qrCode}><QRCode value={publicUrl} size={92} /></View>
      </Panel>
    </View>
  );
}

function normalizeEvent(record: Record<string, string>): CentralPublicEvent {
  return { address: record.address ?? "", endAt: record.endAt ?? "", id: record.id ?? "", livestreamUrl: record.livestreamUrl ?? "", startAt: record.startAt ?? "", title: record.title ?? "", type: record.type ?? "service", venue: record.venue ?? "" };
}

function operationKey(mode: EventMode) {
  const random = typeof crypto !== "undefined" && typeof crypto.randomUUID === "function" ? crypto.randomUUID().replaceAll("-", "") : Math.random().toString(36).slice(2);
  return `${mode === "rsvp" ? "rsvp" : "checkin"}_${Date.now()}_${random}`.slice(0, 150);
}

function formatDateTime(value: string) {
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? value : new Intl.DateTimeFormat(undefined, { dateStyle: "full", timeStyle: "short" }).format(date);
}

function formatTime(value: string) {
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? value : new Intl.DateTimeFormat(undefined, { timeStyle: "short" }).format(date);
}

const styles = StyleSheet.create({
  checkbox: { borderColor: central.border.strong, borderRadius: 4, borderWidth: 1, color: central.text.primary, fontFamily: "Manrope_700Bold", fontSize: 15, height: 24, lineHeight: 22, textAlign: "center", width: 24 },
  consentRow: { alignItems: "flex-start", flexDirection: "row", gap: spacing.sm, minHeight: 44 },
  consentText: { color: central.text.secondary, flex: 1, fontFamily: "Manrope_500Medium", fontSize: 13, lineHeight: 20 },
  detailRow: { alignItems: "flex-start", flexDirection: "row", gap: spacing.xs },
  detailText: { color: central.text.secondary, flex: 1, fontFamily: "Manrope_500Medium", fontSize: 14, lineHeight: 21 },
  directionText: { color: central.text.secondary, fontFamily: "Manrope_400Regular", fontSize: 13, lineHeight: 20 },
  error: { color: central.status.error.foreground, fontFamily: "Manrope_600SemiBold", fontSize: 13, lineHeight: 19 },
  eventPanel: { gap: spacing.sm },
  eventTime: { color: central.text.secondary, fontFamily: "Manrope_600SemiBold", fontSize: 14 },
  eventTitle: { color: central.text.primary, fontFamily: "Manrope_700Bold", fontSize: 22, lineHeight: 27 },
  field: { flexBasis: 260, flexGrow: 1, minWidth: 0 },
  fields: { flexDirection: "row", flexWrap: "wrap", gap: spacing.md },
  formHeading: { alignItems: "flex-start", flexDirection: "row", gap: spacing.sm },
  formHeadingCopy: { flex: 1, gap: spacing.xs },
  formPanel: { gap: spacing.md },
  formTitle: { color: central.text.primary, fontFamily: "Manrope_700Bold", fontSize: 17, lineHeight: 22 },
  fullField: { flexBasis: "100%", flexGrow: 1 },
  heading: { gap: spacing.xs },
  locationCopy: { alignItems: "flex-start", flex: 1, gap: spacing.xs, minWidth: 0 },
  mapFallback: { color: central.text.muted, fontFamily: "Manrope_400Regular", fontSize: 12, lineHeight: 18 },
  modeRow: { flexDirection: "row", flexWrap: "wrap", gap: spacing.sm },
  muted: { color: central.text.muted, fontFamily: "Manrope_400Regular", fontSize: 13, lineHeight: 20 },
  panel: { gap: spacing.sm },
  pills: { flexDirection: "row", flexWrap: "wrap", gap: spacing.xs },
  qrCode: { backgroundColor: "#ffffff", padding: spacing.sm },
  qrCopy: { flex: 1, gap: spacing.xs, minWidth: 220 },
  qrPanel: { alignItems: "center", flexDirection: "row", flexWrap: "wrap", gap: spacing.md, justifyContent: "space-between" },
  section: { gap: spacing.md },
  success: { alignItems: "flex-start", flexDirection: "row", gap: spacing.md },
  successCopy: { flex: 1, gap: spacing.xs },
  successTitle: { color: "#10265E", fontFamily: "Manrope_700Bold", fontSize: 18 },
  title: { color: central.text.primary, fontFamily: "Manrope_700Bold", fontSize: 24, lineHeight: 29 },
  url: { color: central.action.primary, fontFamily: "Manrope_600SemiBold", fontSize: 11, lineHeight: 17 },
});
